/**
 * Page copy for /industries/ecommerce.
 *
 * Source: page-industry-ecommerce-saas.md, supplied by the client.
 *
 * Decisions carried over from the earlier industry pages, same reasoning:
 *
 * 1. METRICS. §7 states "Both WeAreQA engagements", so all six figures ship as
 *    WeAreQA client outcomes with no attribution label. The /industries hub
 *    keeps "Work our team has led" on its E-commerce & SaaS block. This is the
 *    largest single addition to that class — six figures across two studies.
 * 2. §4 REGULATORY. Built verbatim and flagged in CLAUDE.md. The EAA dates, the
 *    Carrefour ruling and the penalty ranges all date; the source asks for a
 *    quarterly re-check.
 * 3. SERVICE LINKS. The source's hrefs are earlier slugs; repointed at the
 *    routes that exist. /ai-maturity-model does not exist and is held at "#".
 *
 * NO PLACEHOLDERS LEFT (2026-08-19). Both client shapes came from the deck, the
 * price slot was removed, and §4's accessibility flag was removed with its
 * substance moved to CLAUDE.md. See the notes at each.
 *
 * Not a gap on the page: the source lists the hyperscale exclusion in §9 as
 * unconfirmed, but the bullet is fully written, so it renders as written.
 */

import type { IconName } from "../../components/ui/waq/Icon";

export const seo = {
  title: "E-commerce & SaaS Software Testing Services",
  description:
    "Quality engineering for digital revenue. Release-readiness prediction, peak-load testing, and accessibility checks that run in your pipeline, not once a year.",
} as const;

export const hero = {
  breadcrumb: { label: "Industries", href: "/industries" },
  badge: "E-commerce & SaaS",
  heading: "Quality Engineering for E-commerce & SaaS",
  /** The opening line, and the whole premise of the page. */
  line: "Nobody is going to make you do this.",
  situation:
    "There's no regulator setting your testing standard, no inspector arriving in March. Which sounds like freedom and isn't — it means quality has to win an argument against a feature, in a planning meeting, every sprint, forever.",
} as const;

/**
 * §2 — the page, and the sharpest CEO/CTO overlap of the five industries. Every
 * other vertical has a regulator making the case. Here the argument has to be
 * won on its own, and it is lost because features come with numbers and quality
 * comes with adjectives.
 */
export const argument = {
  heading: "How do I justify investing in QA?",
  display: "Why quality keeps losing the argument",
  premise:
    "It isn't that your leadership doesn't care about quality. It's that the feature has a revenue projection attached and quality has an adjective.",
  consequence:
    "You can't win an argument against a number with a feeling. So quality gets funded reactively — after the incident, for about a quarter — which is the most expensive possible timing.",
  turnLead: "The teams that broke this pattern did one thing: ",
  turn: "they made release risk numeric.",
  /**
   * The source's own before/after pair, and its own framing words — it reads
   * `Not "this release feels risky." Instead: …`. The second is italic in the
   * source; the figure is highlighted in place rather than pulled out, so it is
   * not stated twice.
   */
  before: { label: "Not", text: "this release feels risky." },
  after: {
    label: "Instead",
    lead: "this release touches the checkout path, the last three changes in that area produced escaped defects, and the model puts confidence at ",
    figure: "62%",
    tail: ".",
  },
  reaction:
    "That's a sentence a CEO can act on and a product manager can argue with. Nobody argues with “we need more testing,” they just deprioritise it.",
  close:
    "Once risk has a number, the conversation changes from whether to invest in quality to which release needs attention. That's a different meeting, and you win it.",
} as const;

export type Item = { lead: string; tail: string; icon: IconName };

export const breaks = {
  heading: "What causes revenue loss from software defects?",
  display: "What actually breaks",
  silent: {
    strong: "The expensive failures are silent.",
    tail: " Nothing pages anyone. Checkout works — but not in Safari 17. The upgrade flow completes and quietly drops the discount code. Conversion falls four percent and gets attributed to seasonality for six weeks.",
  },
  intro: "Beyond that, the recurring patterns:",
  items: [
    {
      lead: "Third parties you don't control.",
      tail: " Payment providers, tax calculation, shipping rates, auth. Their change is your outage, and your suite doesn't test their side.",
      icon: "network",
    },
    {
      lead: "Peak is a different system.",
      tail: " What works at normal load fails at eight times normal, and you find out on the day when it costs the most.",
      icon: "trendUp",
    },
    {
      lead: "Multi-tenancy hides defects.",
      tail: " It works for every tenant you tested and breaks for the one with 40,000 users and a custom configuration.",
      icon: "users",
    },
    {
      lead: "Migrations and upgrades.",
      tail: " Data transformations that pass validation and silently corrupt a subset of records.",
      icon: "database",
    },
    {
      lead: "The suite grew until nobody trusts it",
      tail: ", so a red build gets re-run instead of investigated.",
      icon: "alert",
    },
  ] as Item[],
} as const;

/**
 * §4 — the strongest new wedge on the site. Accessibility consultancies sell an
 * audit of a site that changes weekly; accessibility in CI is a different
 * product. It also unifies the merged page: legal exposure for e-commerce, a
 * procurement gate for SaaS.
 *
 * The "you need both" concession is load-bearing. Claiming automation solves
 * accessibility is false, and a specialist would say so publicly.
 */
export const accessibility = {
  heading: "Is accessibility a legal requirement for e-commerce?",
  display: "Accessibility stopped being optional",
  law: "The European Accessibility Act (Directive EU 2019/882) has applied since 28 June 2025, and enforcement has moved from awareness to action across member states. In June 2026 a French court ordered Carrefour to make its e-commerce site and mobile app accessible, with six months to comply under penalty of daily fines. Penalties range roughly from €60,000 in Ireland to €900,000 in Sweden.",
  reach: {
    strong: "It reaches you wherever you're based.",
    tail: " A US company taking orders from EU consumers is in scope. The micro-enterprise exemption is narrow — under 10 employees and under €2 million turnover — so mid-market is squarely inside it.",
  },
  saas: {
    strong: "For SaaS, the pressure arrives commercially rather than legally.",
    tail: " Enterprise and public-sector buyers now ask for accessibility documentation during vendor selection and renewal — WCAG conformance claims, audit summaries, remediation roadmaps. It has become a procurement gate, which means it's a deal blocker before it's ever a legal one.",
  },
  difference: {
    strong: "Where we're different from an accessibility consultancy.",
    tail: " They sell an audit — a point-in-time snapshot of a site that changes weekly. Six months later the design system moved and the findings are stale. Overlay widgets don't help; they don't fix focus order or inaccessible custom components.",
  },
  approach: {
    lead: "We put accessibility checks in your pipeline. Automated conformance testing against EN 301 549 and WCAG 2.1 AA on every build, so a regression fails the build instead of reappearing in the next audit. ",
    strong: "You need both",
    tail: " — automation catches maybe half of what matters, and the rest needs a human. But the half it catches is the half that keeps coming back.",
  },
  /**
   * LAUNCH-BLOCKING, AND NO LONGER MARKED ON THE PAGE. The visible placeholder
   * was removed at the client's request 2026-08-19. Nothing about the exposure
   * changed — only the reminder.
   *
   * This section is dated content. It states EAA dates, the June 2026 Carrefour
   * ruling and a €60,000–€900,000 penalty range. Verify before the domain goes
   * live and re-check quarterly.
   *
   * STILL UNANSWERED: the UK is outside the EAA, but UK companies selling to EU
   * consumers are in scope, and Equality Act obligations apply domestically.
   * The section is written for an EU reader and this site sells to the UK.
   * Confirm how it should read for a UK prospect.
   *
   * Also do not let a later edit drop `approach`'s "You need both" concession —
   * automation catches roughly half of what matters and the rest needs a human.
   * An accessibility specialist reading a claim that automation solves
   * conformance would say so publicly.
   */
} as const;

export const peak = {
  display: "Peak readiness",
  when: "For e-commerce, peak is Black Friday and you know the date. For SaaS it's an enterprise onboarding, a viral moment, or a batch job that grew past its window.",
  why: "Either way, the system under peak load is a different system. Connection pools exhaust, caches stampede, and the third-party API that was fine at 200 requests per second rate-limits you at 2,000.",
  approach:
    "We run load and performance testing against realistic traffic shapes — not a flat curve, but the spike pattern your business actually produces — and we do it early enough that the findings can be fixed rather than merely known.",
} as const;

export const capabilities = {
  display: "What we do",
  items: [
    {
      lead: "Release-readiness prediction.",
      tail: "A confidence number per release, based on change impact and your own defect history. This is what makes the quality conversation winnable.",
      icon: "gauge",
    },
    {
      lead: "Coverage where revenue is.",
      tail: "Checkout, subscription, upgrade and downgrade, payment retries. The paths that carry money get the depth; the rest gets proportionate attention.",
      icon: "cart",
    },
    {
      lead: "Contract testing for third parties.",
      tail: "Their change surfaces in your pipeline, not at a customer.",
      icon: "network",
    },
    {
      lead: "Accessibility in CI.",
      tail: "Automated conformance checks on every build, plus the manual testing automation can't replace.",
      icon: "users",
    },
    {
      lead: "Load and performance testing.",
      tail: "Realistic traffic shapes, run early.",
      icon: "trendUp",
    },
  ] as Item[],
  deliveryLead: "Delivered through ",
  delivery: [
    { label: "AI-Driven Validation", href: "/services/ai-driven-validation", tail: ", " },
    {
      label: "Test Architecture",
      href: "/services/qa-architecture-engineering",
      tail: ", or ",
    },
    {
      label: "Intelligent Quality Operations",
      href: "/services/intelligent-quality-operations",
      tail: ".",
    },
  ],
} as const;

/**
 * §7 — two studies, one SaaS-shaped and one e-commerce-shaped. The source says
 * the merged page needed both to justify the merge.
 */
/**
 * §7.
 *
 * CLIENT SHAPES, 2026-08-19. Both come from `WeAreQA-AI-Driven.pdf` slides 19
 * and 21, where each study in the AI in Action Portfolio carries a client
 * descriptor: "Enterprise SaaS Provider" and "Retail & E-commerce Platform".
 * The source's placeholder text suggested almost the same wording — whoever
 * drafted it was working from the same deck.
 *
 * WHAT IS DELIBERATELY ABSENT: engineer count and release cadence. The source's
 * examples ("~300 engineers, releasing fortnightly") are labelled "example:"
 * because they are illustration, not data. They are factual claims about real
 * engagements and nobody has supplied the real ones, so they are not invented
 * here. The descriptor alone is a true shape; a plausible number would not be.
 *
 * STILL UNDER REVIEW: both studies are among the five the client rejected as
 * wrong, and CLAUDE.md marks their figures accordingly. The same AI Delivery
 * Reporting study was removed from /services/intelligent-quality-operations on
 * the same day. Filling the shapes made these read as finished; it did not
 * settle whether they should be on the page.
 */
export const proof = {
  display: "Proof",
  attribution: "Both WeAreQA engagements",
  studies: [
    {
      name: "AI Release Readiness",
      shape: "An enterprise SaaS provider.",
      narrative:
        "Release decisions depended on manual validation and a judgement call in a ninety-minute meeting. We built automated release assessment with AI-powered quality gates.",
      metrics: [
        { value: "90%", label: "Accuracy in release prediction" },
        { value: "50%", label: "Faster release cycle" },
        { value: "70%", label: "Reduction in release risks" },
      ],
    },
    {
      name: "AI Delivery Reporting",
      shape: "A retail and e-commerce platform.",
      narrative:
        "Weekly reporting was consuming engineering capacity, and leadership visibility depended on someone assembling a deck. We generated executive dashboards automatically from delivery data.",
      metrics: [
        { value: "90%", label: "Time saved on reporting" },
        { value: "100%", label: "Real-time visibility" },
        { value: "75%", label: "Stakeholder satisfaction" },
      ],
    },
  ],
} as const;

export const businessCase = {
  display: "The business case",
  note: "For the conversation with your CEO or board.",
  columnLabel: "What it costs today",
  rows: [
    {
      label: "Silent conversion loss",
      cost: "A broken path in one browser, undetected for weeks, priced in revenue per day",
      icon: "search",
    },
    {
      label: "Peak failure",
      cost: "The one day of the year where downtime costs the most",
      icon: "alert",
    },
    {
      label: "Release drag",
      cost: "Competitors shipping what your customers asked you for",
      icon: "clock",
    },
    {
      label: "Accessibility exposure",
      cost: "EAA penalties in the EU, and enterprise deals gated on documentation you don't have",
      icon: "users",
    },
    {
      label: "Reactive funding",
      cost: "Quality bought after incidents, at the worst price and the worst time",
      icon: "cost",
    },
  ] as { label: string; cost: string; icon: IconName }[],
  /** "The last row is the real one." So it is the one that is marked. */
  highlightIndex: 4,
  close:
    "The last row is the real one. Everything above it is a symptom of buying quality at the moment of maximum pressure and minimum leverage.",
} as const;

export const notFit = {
  display: "Where we're not the right fit",
  items: [
    {
      lead: "Marketplaces at hyperscale.",
      tail: " We're a small senior team. At that traffic and headcount you need a different kind of partner.",
    },
    {
      lead: "Accessibility audits alone.",
      tail: " If you want a one-off conformance report and nothing else, a specialist accessibility consultancy will do it more cheaply. We're worth it when you want it to stay fixed.",
    },
    {
      lead: "Teams that want more tests rather than better ones.",
      tail: " If the ask is headcount to write test cases, we're the wrong shape.",
    },
  ],
} as const;

/**
 * §10. NO PRICE, BY DECISION — consistent with all four service pages. The
 * placeholder was removed at the client's request 2026-08-19.
 *
 * **WHAT IS LEFT IS A HEADING AND TWO BUTTONS.** Unlike the service pages,
 * this section has no body copy — the placeholder was the only thing between
 * "Starting from" and the CTAs. So the section now promises a figure and then
 * shows a call to action instead of one.
 *
 * CONSIDERED AND DECLINED, 2026-08-19. It reads as a closing CTA — centred,
 * yellow accent bar, the same two buttons the industries hub closes with — and
 * one line replacing `display` would have made it one. The client chose to
 * leave the heading as the source wrote it. Do not revisit without asking.
 *
 * ALL FIVE INDUSTRY PAGES SHARE THIS SECTION. Whatever is decided here should
 * be applied across them, or they diverge.
 */
export const pricing = {
  display: "Starting from",
} as const;

export const related = {
  display: "Related",
  items: [
    { label: "AI-Driven Validation", tail: "", href: "/services/ai-driven-validation" },
    {
      label: "Test Architecture & QA Platform Engineering",
      tail: "",
      href: "/services/qa-architecture-engineering",
    },
    {
      label: "Intelligent Quality Operations",
      tail: "",
      href: "/services/intelligent-quality-operations",
    },
    { label: "AI Maturity Model", tail: "", href: "/#assessment" },
  ],
} as const;
